import { useEffect, useState } from 'react';
import { MARKET_READ_TIMEOUT_MS } from '~/features/market/requestPolicy';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { getData } from '~/utils/service';

export type FitStatus =
  | 'COMPLETED'
  | 'PARTIAL'
  | 'PENDING'
  | 'FAILED'
  | 'NOT_SCORED'
  | 'UNAVAILABLE'
  | 'CLOSED'
  | 'NO_PROVIDERS'
  | 'BELOW_MIN_PROVIDERS';
export type SearchFitSummary = {
  listingId: number;
  status: FitStatus;
  matchScore: number | null;
  scoredCount: number;
  attachedCount: number;
  pendingCount: number;
  failedCount: number;
  notScoredCount: number;
  scoredAt: string | null;
  // Only sent with BELOW_MIN_PROVIDERS (B2B): providers attached vs. the required minimum.
  attachedProviders?: number;
  minimumProviders?: number;
  providers?: {
    responseProviderId: number;
    providerId: number;
    matchScore: number | null;
    status: FitStatus;
    scoredAt: string | null;
  }[];
};
export type SearchFitKind = 'job' | 'b2b';
const EMPTY_SCORES: Record<number, SearchFitSummary> = {};
const POLL_INTERVAL_MS = 10_000;
const MAX_POLLS = 6;
// A page can load before the hourly reconciliation scores its pairs; re-read, never trigger.
const NOT_SCORED_INTERVAL_MS = 60_000;
const NOT_SCORED_REREADS = 5;

export const useSearchFit = (
  kind: SearchFitKind,
  viewerScope: string,
  listingIds: number[],
  revision: unknown
) => {
  const ids = [...new Set(listingIds)]
    .filter((id) => Number.isSafeInteger(id) && id > 0)
    .sort((a, b) => a - b)
    .join(',');
  const key = `${kind}:${viewerScope}:${ids}`;
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{
    key: string;
    revision: unknown;
    attempt: number;
    scores: Record<number, SearchFitSummary>;
    loading: boolean;
    error: boolean;
  }>();
  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let polls = 0;
    let rereads = 0;
    const load = async () => {
      try {
        // Requests are bounded even if a future search increases its page size.
        const allIds = ids ? ids.split(',') : [];
        const scores: SearchFitSummary[] = [];
        for (let index = 0; index < allIds.length; index += 100) {
          const batch = await withRequestDeadline(
            (signal) =>
              getData<SearchFitSummary[]>(
                kind === 'job' ? 'jobFitSearchScores' : 'b2bFitSearchScores',
                { params: { listing_ids: allIds.slice(index, index + 100).join(',') }, signal }
              ),
            MARKET_READ_TIMEOUT_MS,
            controller.signal
          );
          if (controller.signal.aborted) return;
          scores.push(...batch);
        }
        if (controller.signal.aborted) return;
        setResult({
          key,
          revision,
          attempt,
          scores: Object.fromEntries(scores.map((score) => [score.listingId, score])),
          loading: false,
          error: false,
        });
        // The PENDING and NOT_SCORED bounds are separate: spending one never uses up the other.
        if (
          scores.some(
            (score) =>
              score.status !== 'BELOW_MIN_PROVIDERS' &&
              (score.pendingCount > 0 || score.status === 'PENDING')
          ) &&
          polls++ < MAX_POLLS
        ) {
          timer = setTimeout(() => void load(), POLL_INTERVAL_MS);
        } else if (
          scores.some((score) => score.status === 'NOT_SCORED') &&
          rereads++ < NOT_SCORED_REREADS
        ) {
          timer = setTimeout(() => void load(), NOT_SCORED_INTERVAL_MS);
        }
      } catch {
        if (!controller.signal.aborted)
          setResult((previous) => ({
            key,
            revision,
            attempt,
            scores:
              previous?.key === key && previous.revision === revision
                ? previous.scores
                : EMPTY_SCORES,
            loading: false,
            error: true,
          }));
      }
    };
    void load();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [key, ids, kind, revision, attempt]);
  const current = result?.key === key && result.revision === revision ? result : undefined;
  return {
    scores: current?.scores ?? EMPTY_SCORES,
    loading:
      current?.attempt === attempt
        ? current.loading
        : Boolean(ids) && Object.keys(current?.scores ?? EMPTY_SCORES).length === 0,
    error: current?.attempt === attempt ? current.error : false,
    retry: () => setAttempt((value) => value + 1),
  };
};
export type SearchFitResult = ReturnType<typeof useSearchFit>;
